import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, FilePlus2, Loader2, PenLine } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { phoneIsUnusable, phoneProblem } from '@jt/domain';
import { LANDLORD_TERMS, LANDLORD_TERM_LABEL, type LandlordTerm } from '@jt/domain';
import { api } from '../lib/api';
import { HelpHint } from '../components/HelpHint';
import { PageNav } from '../components/PageNav';
import { useToast } from '../components/Toast';
import { useSession } from '../lib/session';
import type { LandlordAgreementRow } from '../lib/types';

/**
 * Permission to cable a building, one row per property.
 *
 * ## Why the raise form insists on the landlord's email
 *
 * Because the next step emails it to them. There is no other way for a
 * landlord to sign — they have no account and should not need one to let us
 * run a cable — so an agreement with no address is one that cannot proceed.
 * Refusing here gives whoever is typing a field to correct; refusing at the
 * moment of signing would hand them a signed agreement with nowhere to send
 * it.
 */
export function LandlordAgreementsPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const canManage = can('landlord_agreement:manage');

  const empty = {
    propertyName: '',
    physicalAddress: '',
    landlordName: '',
    landlordEmail: '',
    landlordPhone: '',
    term: 'one_year' as LandlordTerm,
  };

  const [raising, setRaising] = useState(false);
  const [form, setForm] = useState(empty);

  const all = useQuery({
    queryKey: ['landlord-agreements'],
    queryFn: () => api.landlordAgreements(),
  });

  const raise = useMutation({
    mutationFn: () => api.raiseLandlordAgreement(form),
    onSuccess: async (agreement) => {
      toast.done(`${agreement.reference} is ready for the company to sign.`);
      setRaising(false);
      setForm(empty);
      await queryClient.invalidateQueries({ queryKey: ['landlord-agreements'] });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  if (all.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const rows = all.data?.agreements ?? [];

  /*
   * The email is the one field this form cannot be lenient about, so the
   * button waits until the shape is right rather than letting somebody press
   * it and read a refusal. The server checks the same thing — this is the
   * courtesy, not the rule.
   */
  const emailLooksRight = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(form.landlordEmail.trim());
  const ready =
    form.propertyName.trim().length > 1 &&
    form.landlordName.trim().length > 1 &&
    form.landlordPhone.trim().length > 6 &&
    emailLooksRight;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageNav crumbs={[{ label: 'Customers', to: '/customers' }, { label: 'Building access' }]} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Building access agreements
            <HelpHint>
              A landlord’s written permission to install and maintain our equipment in their
              building. Somebody signs for the business first, and only then is it emailed to the
              landlord — they need no account to sign it.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            One per property. The equipment stays ours, and this is the paper that says so.
          </p>
        </div>

        {canManage && !raising && (
          <button type="button" className="btn-primary" onClick={() => setRaising(true)}>
            <FilePlus2 className="h-4 w-4" aria-hidden="true" />
            Raise an agreement
          </button>
        )}
      </div>

      {raising && (
        <section className="card space-y-3">
          <div>
            <h2 className="font-heading text-lg">Raise an agreement</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              The rest — the fee, the cable routes, what the survey found — is filled in before it
              is signed. This is only what is needed to start one and to reach the landlord.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              Property name
              <input
                className="input mt-1 w-full"
                value={form.propertyName}
                onChange={(event) => setForm({ ...form, propertyName: event.target.value })}
                placeholder="Riverside Court"
              />
            </label>
            <label className="text-sm">
              Physical address
              <input
                className="input mt-1 w-full"
                value={form.physicalAddress}
                onChange={(event) => setForm({ ...form, physicalAddress: event.target.value })}
                placeholder="Nyanchwa, off the Kisii road"
              />
            </label>
            <label className="text-sm">
              Landlord’s full name
              <input
                className="input mt-1 w-full"
                value={form.landlordName}
                onChange={(event) => setForm({ ...form, landlordName: event.target.value })}
              />
            </label>
            <label className="text-sm">
              Landlord’s telephone
              <input
                className="input mt-1 w-full"
                value={form.landlordPhone}
                onChange={(event) => setForm({ ...form, landlordPhone: event.target.value })}
                placeholder="+254712345678"
              />
              {/*
                While it is being typed, not on save. A number corrected in the
                moment costs nothing; one discovered later costs a phone call
                to a landlord to ask what it should have been.
              */}
              {form.landlordPhone.trim().length > 3 && phoneProblem(form.landlordPhone) && (
                <span
                  className={`mt-1 block text-xs ${
                    phoneIsUnusable(form.landlordPhone) ? 'text-danger-700' : 'text-muted-foreground'
                  }`}
                >
                  {phoneProblem(form.landlordPhone)}
                </span>
              )}
            </label>
            <label className="text-sm sm:col-span-2">
              Landlord’s email
              <input
                type="email"
                className={`input mt-1 w-full ${
                  form.landlordEmail && !emailLooksRight ? 'border-accent-400' : ''
                }`}
                value={form.landlordEmail}
                onChange={(event) => setForm({ ...form, landlordEmail: event.target.value })}
                placeholder="landlord@example.com"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                {form.landlordEmail && !emailLooksRight
                  ? 'That does not look like an email address.'
                  : 'Required. The agreement is sent here to be signed — there is no other way in.'}
              </span>
            </label>
            <label className="text-sm">
              How long it runs
              <select
                className="input mt-1 w-full"
                value={form.term}
                onChange={(event) => setForm({ ...form, term: event.target.value as LandlordTerm })}
              >
                {LANDLORD_TERMS.map((term) => (
                  <option key={term} value={term}>
                    {LANDLORD_TERM_LABEL[term]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={!ready || raise.isPending}
              onClick={() => raise.mutate()}
            >
              {raise.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Raise it
            </button>
            <button type="button" className="btn-secondary" onClick={() => setRaising(false)}>
              Cancel
            </button>
            <span className="text-xs text-muted-foreground">
              Nothing reaches the landlord until somebody signs for the business.
            </span>
          </div>
        </section>
      )}

      {rows.length === 0 ? (
        <p className="card text-sm text-muted-foreground">
          No building agreements yet. Raise one before a technician takes a drill to somebody’s
          wall.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <AgreementLine key={row.id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One agreement, as a row.
 *
 * Leads with whose turn it is rather than with the status, because that is the
 * only part anybody acts on. `AWAITING_COMPANY` and `AWAITING_LANDLORD` are
 * the same fact said twice; "waiting for us to sign" is the useful reading.
 */
function AgreementLine({ row }: { row: LandlordAgreementRow }) {
  const badge =
    row.status === 'SIGNED'
      ? { text: 'signed', tone: 'bg-secondary-100 text-secondary-800' }
      : row.status === 'VOIDED'
        ? { text: 'cancelled', tone: 'bg-muted text-muted-foreground' }
        : row.status === 'SUPERSEDED'
          ? { text: 'replaced', tone: 'bg-muted text-muted-foreground' }
          : row.turn === 'company'
            ? { text: 'waiting for us to sign', tone: 'bg-accent-100 text-accent-900' }
            : { text: 'waiting for the landlord', tone: 'bg-primary-100 text-primary-800' };

  const date = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : null;

  return (
    <li>
      <Link
        to={`/landlord-agreements/${row.id}`}
        className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-border p-3 hover:bg-muted/40"
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {row.turn === 'company' ? (
            <PenLine className="h-4 w-4 shrink-0 text-accent-700" aria-hidden="true" />
          ) : (
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{row.propertyName}</span>
            <span className="block text-xs text-muted-foreground">
              {row.reference} · {row.landlord.name}
            </span>
          </span>
        </span>

        <span className="text-xs text-muted-foreground">
          {LANDLORD_TERM_LABEL[row.term]}
          {date(row.endDate) ? ` · to ${date(row.endDate)}` : ''}
        </span>

        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.tone}`}>
          {badge.text}
        </span>
      </Link>
    </li>
  );
}
